import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  SUPPORT_CONTACT_CATEGORY_LABELS,
  SUPPORT_CONTACT_TOPIC_LABELS,
  type ChatMessage,
  type SupportInboxRow,
  type SupportThreadResponse,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { SupportEscalateForm } from "@/components/SupportEscalateForm";
import { SupportReasonDialog } from "@/components/SupportView";
import { ForbiddenError, UnauthorizedError } from "@/lib/clinician-api";
import { useClinicQueues } from "@/lib/clinic-queues";
import { adminPatientHref } from "@/lib/patient-links";
import { isEscalatableMessage } from "@/lib/support-escalation";
import { fetchSupportAccessStatus } from "@/lib/support-access-api";
import {
  fetchSupportEscalationOptions,
  fetchSupportInbox,
  fetchSupportThread,
  markSupportThreadRead,
  sendSupportReply,
} from "@/lib/support-inbox-api";
import { SUPPORT_REASON_REQUIRED } from "@/lib/support-view";

// TES-1102 (Simon 2026-10-04) — the Support inbox: the staff side of the
// two-way "Ella Support" thread every patient has from signup.
//
// The list shows who wrote and when, never what: it is read without a
// reason. Opening a thread is a read of the patient's data, so it asks for
// today's support access reason first (TES-1040), exactly as the patient page
// does; one reason covers the thread and the patient page for the day. The
// patient sees every reply as "Ella Support"; staff see who wrote it.

const POLL_INTERVAL_MS = 15_000;

function fmtTime(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function SupportInboxRoute() {
  const { patientId } = useParams<{ patientId?: string }>();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [rows, setRows] = useState<SupportInboxRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { threads } = await fetchSupportInbox();
      setRows(threads);
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    const handle = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Support</h1>
      <p className="text-sm text-textSecondary max-w-2xl">
        Patienternas tråd med Ella Support. Patienten ser alla svar som Ella.
        Att öppna en tråd kräver en orsak, som loggas i åtkomstgranskningen.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      <div className="grid gap-md lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="flex flex-col gap-xs">
          {rows && rows.length === 0 ? (
            <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
              Inga meddelanden än.
            </div>
          ) : null}
          {rows?.map((r) => (
            <button
              key={r.patientId}
              type="button"
              aria-current={r.patientId === patientId ? "true" : undefined}
              onClick={() =>
                navigate(`/support-inbox/${encodeURIComponent(r.patientId)}`)
              }
              className={`bg-surface border rounded-md p-md flex items-center gap-md text-left hover:border-brand ${
                r.patientId === patientId
                  ? "border-brand"
                  : "border-surfaceMuted"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {r.patientName}
                </span>
                <span className="block text-xs text-textSecondary">
                  {r.lastSenderType === "patient"
                    ? "Patienten skrev"
                    : "Ella Support skrev"}{" "}
                  {fmtTime(r.lastMessageAt)}
                </span>
                {r.waitingSince ? (
                  <span className="block text-xs text-warning">
                    Väntar på svar sedan {fmtTime(r.waitingSince)}
                  </span>
                ) : null}
              </span>
              {r.unreadCount > 0 ? (
                <span className="min-w-[22px] h-[22px] px-xs rounded-full bg-brand text-white text-xs flex items-center justify-center">
                  {r.unreadCount > 99 ? "99+" : r.unreadCount}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <div>
          {patientId ? (
            <SupportThread
              key={patientId}
              patientId={patientId}
              onChanged={() => void load()}
            />
          ) : (
            <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
              Välj en tråd för att läsa och svara.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type AccessState = "checking" | "reason" | "granted" | "cancelled" | "error";

function SupportThread({
  patientId,
  onChanged,
}: {
  patientId: string;
  onChanged: () => void;
}) {
  const { signOut, claims } = useAuth();
  const queues = useClinicQueues();
  const reloadQueues = queues.reload;
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [access, setAccess] = useState<AccessState>("checking");
  const [thread, setThread] = useState<SupportThreadResponse | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // TES-1110 — the escalate form: open for the whole thread (no messageId)
  // or for one patient message. `redirectReply` is the approved standard
  // reply, null while none exists (the form then does not offer it).
  const [escalating, setEscalating] = useState<{ messageId?: string } | null>(
    null,
  );
  const [escalationNotice, setEscalationNotice] = useState<string | null>(null);
  const [redirectReply, setRedirectReply] = useState<string | null>(null);

  const fail = useCallback((e: unknown) => {
    if (e instanceof UnauthorizedError) {
      void signOutRef.current();
      return;
    }
    // Today's reason ran out mid-session (it ends with the Stockholm day).
    if (e instanceof ForbiddenError && e.code === SUPPORT_REASON_REQUIRED) {
      setAccess("reason");
      return;
    }
    setError(e instanceof Error ? e.message : String(e));
  }, []);

  useEffect(() => {
    let live = true;
    fetchSupportAccessStatus(patientId)
      .then((s) => {
        if (live) setAccess(s.active ? "granted" : "reason");
      })
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) {
          void signOutRef.current();
          return;
        }
        if (live) setAccess("error");
      });
    return () => {
      live = false;
    };
  }, [patientId]);

  // The newest patient message already marked read, so the 15-second poll
  // only marks (and refreshes the list and the badge) when she wrote again.
  const markedUpTo = useRef<string | null>(null);
  const loadThread = useCallback(async () => {
    try {
      const t = await fetchSupportThread(patientId);
      setThread(t);
      setError(null);
      const newest = t.messages
        .filter((m) => m.senderType === "patient")
        .at(-1);
      if (newest && newest.id !== markedUpTo.current) {
        markedUpTo.current = newest.id;
        await markSupportThreadRead(patientId);
        onChanged();
        reloadQueues();
      }
    } catch (e) {
      fail(e);
    }
  }, [patientId, fail, onChanged, reloadQueues]);

  useEffect(() => {
    if (access !== "granted") return;
    void loadThread();
    const handle = setInterval(() => void loadThread(), POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [access, loadThread]);

  useEffect(() => {
    if (access !== "granted") return;
    let live = true;
    fetchSupportEscalationOptions()
      .then((o) => {
        if (live) setRedirectReply(o.redirectReply);
      })
      // Without the options the form still escalates; it only cannot offer
      // the redirect reply.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [access]);

  function openEscalation(messageId?: string) {
    setEscalationNotice(null);
    setEscalating({ messageId });
  }

  function renderEscalateForm(messageId?: string) {
    return (
      <SupportEscalateForm
        patientId={patientId}
        messageId={messageId}
        redirectReply={redirectReply}
        onCancel={() => setEscalating(null)}
        onError={fail}
        onDone={(outcome) => {
          setEscalating(null);
          setEscalationNotice(outcome);
          void loadThread();
          onChanged();
        }}
      />
    );
  }

  async function send() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await sendSupportReply(patientId, body);
      setDraft("");
      await loadThread();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  if (access === "checking") {
    return <p className="text-sm text-textSecondary">Hämtar…</p>;
  }
  if (access === "reason") {
    return (
      <>
        <p className="text-sm text-textSecondary">
          En orsak krävs för att öppna tråden.
        </p>
        <SupportReasonDialog
          patientId={patientId}
          onGranted={() => setAccess("granted")}
          onCancel={() => setAccess("cancelled")}
        />
      </>
    );
  }
  if (access === "cancelled" || access === "error") {
    return (
      <div className="flex flex-col gap-sm items-start">
        <p className="text-sm text-textSecondary">
          {access === "cancelled"
            ? "Tråden öppnades inte. En orsak krävs för att se patientens meddelanden."
            : "Kunde inte kontrollera åtkomsten till patienten."}
        </p>
        <button
          type="button"
          onClick={() => setAccess("reason")}
          className="text-sm text-brand hover:underline"
        >
          Ange orsak
        </button>
      </div>
    );
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <header className="flex items-center justify-between gap-md">
        <h2 className="font-display text-xl truncate">
          {thread?.patient.name ?? "…"}
        </h2>
        <div className="flex items-center gap-md shrink-0">
          {/* TES-1110 — Ella Support is not an emergency channel; a medical
              question is handed to the care team from here. */}
          <button
            type="button"
            onClick={() => openEscalation()}
            className="text-sm text-brand hover:underline"
          >
            Eskalera till vårdteamet
          </button>
          <Link
            to={adminPatientHref(claims?.role, patientId)}
            className="text-sm text-brand hover:underline"
          >
            Öppna patienten
          </Link>
        </div>
      </header>

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      {escalationNotice ? (
        <p role="status" className="text-sm text-textPrimary">
          {escalationNotice}
        </p>
      ) : null}
      {escalating && !escalating.messageId
        ? renderEscalateForm(undefined)
        : null}

      <ol className="flex flex-col gap-sm" aria-label="Meddelanden">
        {thread && thread.messages.length === 0 ? (
          <li className="text-sm text-textSecondary">
            Inga meddelanden än. Det du skriver visas för patienten som Ella
            Support.
          </li>
        ) : null}
        {thread?.messages.map((m) => (
          <Bubble
            key={m.id}
            message={m}
            staffNames={thread.staffNames}
            onEscalate={
              isEscalatableMessage(m) ? () => openEscalation(m.id) : undefined
            }
          >
            {escalating?.messageId === m.id ? renderEscalateForm(m.id) : null}
          </Bubble>
        ))}
      </ol>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        className="flex flex-col gap-sm"
      >
        <label className="flex flex-col gap-xs text-sm">
          <span className="sr-only">Svar som Ella Support</span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            placeholder="Svara som Ella Support"
            className="rounded-md border border-surfaceMuted bg-background p-sm text-sm"
          />
        </label>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!draft.trim() || busy}
            className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Skickar…" : "Skicka"}
          </button>
        </div>
      </form>
    </section>
  );
}

function Bubble({
  message,
  staffNames,
  onEscalate,
  children,
}: {
  message: ChatMessage;
  staffNames: Record<string, string>;
  /** TES-1110 — set on a patient message that can be escalated. */
  onEscalate?: () => void;
  /** The escalate form, when it is open for this message. */
  children?: ReactNode;
}) {
  const fromPatient = message.senderType === "patient";
  const who = fromPatient
    ? "Patienten"
    : message.senderId
      ? `Ella Support · ${staffNames[message.senderId] ?? "Personal"}`
      : "Ella Support · automatiskt";
  return (
    <li
      className={`max-w-[85%] rounded-md p-sm text-sm ${
        fromPatient
          ? "self-start bg-background border border-surfaceMuted"
          : "self-end bg-brand/10"
      }`}
    >
      <p className="text-xs text-textSecondary">
        {who} · {fmtTime(message.createdAt)}
        {message.editedAt ? " · Redigerat" : ""}
        {/* TES-1131 — the category she chose in "Kontakta Ella". */}
        {message.payload?.supportCategory
          ? ` · ${SUPPORT_CONTACT_CATEGORY_LABELS[message.payload.supportCategory]}`
          : ""}
        {/* TES-1636 — and the subject the app sent her with, if any. */}
        {message.payload?.supportTopic
          ? ` · ${SUPPORT_CONTACT_TOPIC_LABELS[message.payload.supportTopic] ?? message.payload.supportTopic}`
          : ""}
      </p>
      <p className="whitespace-pre-wrap">{message.body}</p>
      {onEscalate && !children ? (
        <button
          type="button"
          onClick={onEscalate}
          className="mt-xs text-xs text-brand hover:underline"
        >
          Eskalera till vårdteamet
        </button>
      ) : null}
      {children ? <div className="mt-sm">{children}</div> : null}
    </li>
  );
}
